// import lib(s)
import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import styles from "./Annotator.module.css"
import { useTheme } from "@emotion/react"

import ROUTES from "../../route/routes"
import { useNavigate, useParams } from "react-router-dom"

import { useAxiosPrivate, useDocumentTitle } from "../../hooks"
import { PAGE_TITLE } from "../../constants/constants"
import { FIDUCIAL_POINT_TYPES } from "../../constants/annotator"
import { computeFiducialIntervals, buildFiducialInputSignature, extractMedianBeatWindowsByType } from "../../utils/fiducialIntervals"
import { buildMedianBeatsField, buildFiducialPointLegend } from "../../utils/medianBeatSerialization"

import Layout from "../../layouts/common/Layout"
import PlaceholderText from "../../components/common/SampleText"
import Loader from "../../components/common/Loader"
import toast from "react-hot-toast"

import Tabs from "@mui/material/Tabs"
import Tab from "@mui/material/Tab"
import Box from "@mui/material/Box"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Typography from "@mui/material/Typography"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogActions from "@mui/material/DialogActions"
import Autocomplete from "@mui/material/Autocomplete"
import Divider from "@mui/material/Divider"
import TextField from "@mui/material/TextField"
import FormLabel from "@mui/material/FormLabel"
import FormControl from "@mui/material/FormControl"
import FormGroup from "@mui/material/FormGroup"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormHelperText from "@mui/material/FormHelperText"
import Checkbox from "@mui/material/Checkbox"
import Switch from "@mui/material/Switch"
import Slider from "@mui/material/Slider"
import Link from "@mui/material/Link"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import { Grid } from "@mui/material"

import copy2cliboard from "../../utils/copy2cliboard"
import JsonViewer from "./views/JsonView"
import TaskRecordings from "./TaskRecordings"

import { METHODS, errorHandler } from "../../hooks/useFetch"

import { removeBeat, removeIntr, updateIntr, setAllBeatLabels, clearAllAnnotations, setInverted, removeFiducialPoint, clearFiducialPoints, setMedianBeatSingleColumn, setMedianBeatVerticalSpacing, setMedianBeatLeftMs, setMedianBeatRightMs, setMedianBeatBeatType, setMedianBeatWindowByType, setMedianBeatWindowLoadedForRecordingId, selectCurrentFiducialPoints, selectCurrentBeatType, selectCurrentMedianBeatWindow } from "../Annotator/AnnotatorSlice"
import Chip from "@mui/material/Chip"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import Menu from "@mui/material/Menu"
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown"

const RightPanel = () => {

	const dispatch = useDispatch()
    const navigate = useNavigate()
	const urlParams = useParams()
	const theme = useTheme()
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [AlgAnnotations, setAlgAnnotations] = useState({})
	const [AlgDet, setAlgDet] = useState([])
	const [DoctorNote, setDoctorNote] = useState("")
	const [staleFiducialDialogOpen, setStaleFiducialDialogOpen] = useState(false)
	const [staleFiducialTypes, setStaleFiducialTypes] = useState([])
	const appliedInversionForRawData = useRef(null)
	const appliedMedianBeatWindowForRawData = useRef(null)

	const axiosPrivate = useAxiosPrivate()


	const isTurkTask = !(urlParams["task_id"] === "open");

	const FileRawData = useSelector((state) => state.Annotator.rawData)
	const Annotator = useSelector((state) => state.Annotator)
	const currentFiducialPoints = useSelector(selectCurrentFiducialPoints)
	const currentBeatType = useSelector(selectCurrentBeatType)
	const currentWindow = useSelector(selectCurrentMedianBeatWindow)

	// Beat-type dropdown options — memoized so the normalization effect below
	// and the render both see the same list. Only beat types with ≥2 beats
	// are offered (single beats produce an empty median).
	const beatTypeDropdownOptions = React.useMemo(() => {
		const counts = new Map()
		for (const b of (Annotator.annotations.beats || [])) {
			if (!b?.t || b.t === "x") continue
			counts.set(b.t, (counts.get(b.t) || 0) + 1)
		}
		const options = (Annotator.annotations.labels?.beats || [])
			.filter((b) => b.slug !== "ddf" && (counts.get(b.slug) || 0) > 1)
			.map((b) => ({ ...b, count: counts.get(b.slug) || 0 }))
		return options
	}, [Annotator.annotations.beats, Annotator.annotations.labels?.beats])

	// If the current beat type is no longer a valid dropdown option
	// (e.g., its beats were relabeled away), sync Redux to the first option so
	// the dropdown, header, and median-beat computation all agree.
	useEffect(() => {
		if (Annotator.leadCount === 12) return
		if (beatTypeDropdownOptions.length === 0) return
		if (beatTypeDropdownOptions.some((o) => o.slug === currentBeatType)) return
		dispatch(setMedianBeatBeatType(beatTypeDropdownOptions[0].slug))
	}, [dispatch, Annotator.leadCount, beatTypeDropdownOptions, currentBeatType])
	const auth = useSelector((state) => state.auth)

	const kai12 = Annotator?.rawData?.data?.kai12Analysis

	useEffect(() => {
	if(isTurkTask) {
		let tempAlgDet = [];
		(Annotator.annotations.algDet || []).map((alg) => {
			return Object.entries(alg).forEach(([key, value]) => (
				tempAlgDet.push(
					<Typography key={key} variant="p" component="div">
						<b>{key}</b> : {value}
					</Typography>
				)
			));
		})
		setAlgDet(tempAlgDet)
		let tempAlgAnnotations = {}
		Annotator.annotations.global.map((globalAnn) => {
			return Object.entries(globalAnn).forEach(([key, value]) => (
				tempAlgAnnotations[key] = value
			));
		})
		// Always apply per-recording inversion (default false) so the setting can't leak
		// from the previously opened recording. Force false for 12-lead: the toggle is
		// hidden there, so any persisted true would be unclearable in the UI.
		const loadedIsInverted = Annotator.leadCount !== 12 && !!tempAlgAnnotations.isInverted
		if (appliedInversionForRawData.current !== Annotator.currentRecordingId) {
			dispatch(setInverted(loadedIsInverted))
			appliedInversionForRawData.current = Annotator.currentRecordingId
		}
		delete tempAlgAnnotations.isInverted
		// Restore the median-beat window per-recording. Only meaningful for non-12-lead
		// (12-lead median beats come pre-computed from kai12Analysis and ignore this
		// window entirely). Missing/stale values fall back to slice defaults so the
		// previous recording's window doesn't leak into the new one.
		if (appliedMedianBeatWindowForRawData.current !== Annotator.currentRecordingId) {
			// Restore every saved median-beat window into its per-beat-type
			// bucket. Preserving all windows (not just the current beat type's)
			// is important so the staleness signatures rebuilt after this
			// dispatch match what was saved on disk — otherwise switching to a
			// beat type whose window wasn't restored would falsely flag its
			// fiducial points as stale.
			if (Annotator.leadCount !== 12) {
				const rawMb = Annotator?.rawData?.data?.annotations?.medianBeats
				const rawFp = Annotator?.rawData?.data?.annotations?.medianBeatFiducialPoints
				const savedWindows = extractMedianBeatWindowsByType(rawMb || rawFp)
				dispatch(setMedianBeatWindowByType(savedWindows))
			}
			// Publish the marker to Redux so MedianBeats knows the settings are stable
			// for this recording; the fiducial-point prune loop gates on this to avoid
			// wiping points outside ±the default window during the brief "defaults
			// still applied" window on load.
			dispatch(setMedianBeatWindowLoadedForRecordingId(Annotator.currentRecordingId))
			appliedMedianBeatWindowForRawData.current = Annotator.currentRecordingId
		}
		setAlgAnnotations(tempAlgAnnotations)
		setDoctorNote(Annotator?.annotations?.doctorNote || "")
		setCurrentTab(1)
	} else {
		setCurrentTab(2)
	}
	}, [Annotator.rawData])
	

	const copyCurrentUrl = () => {
		copy2cliboard(window.location.href);
		toast.success("Copied to clipboard!")
	}

	const [bulkAnchorEl, setBulkAnchorEl] = useState(null)
	const bulkMenuOpen = Boolean(bulkAnchorEl)

	const handleClearAll = () => {
		dispatch(clearAllAnnotations())
		setAlgAnnotations(Object.fromEntries(Object.keys(AlgAnnotations).map((k) => [k, false])))
		setDoctorNote("")
		setBulkAnchorEl(null)
	}

	const handleSetBeats = (slug) => {
		dispatch(setAllBeatLabels(slug))
		setBulkAnchorEl(null)
	}

	const performSave = async ({ dropFiducialTypes = null } = {}) => {
		// dropFiducialTypes=[slug,...] clears the named buckets (used by the
		// stale-confirm dialog).
		if (Array.isArray(dropFiducialTypes) && dropFiducialTypes.length > 0) {
			dispatch(clearFiducialPoints(dropFiducialTypes))
		}
		setLoading(true)
		try {
			let data = JSON.parse(JSON.stringify(Annotator.rawData));
			if (!Array.isArray(data.data.annotator)) {
				data.data.annotator = []
			}
			data.data.annotator.push({
				time: new Date().toISOString(),
				person: auth.user.email
			})
			data.data.annotationResults = {questionCorrect : true}
			// now write annotations
			// data.data.annotations.beatLabels
			// [{label:"n",sample:209,duration:1}]
			// TODO: Try to make this backward compatible
			data.data.annotations.beatsV2 = Annotator.annotations.beats

			// ========================================================================
			// data.data.annotations.global
			// [{"hr":true}]
			let fGlobal = []
			Object.entries(AlgAnnotations).forEach(([key, value]) => (
				fGlobal.push({[key]: value})
			));
			// Only persist isInverted for lead configurations where the toggle is exposed.
			// 12-lead recordings hide the UI, so saving the flag there would strand the
			// recording in an inverted state with no way to disable it.
			if (Annotator.leadCount !== 12) {
				fGlobal.push({isInverted: Annotator.settings.inverted})
			}
			data.data.annotations.global = fGlobal
			// ========================================================================
			// data.data.annotations.intervals
			// [{label:"Noisy",start:209,end:1}]
			// TODO: Try to make this backward compatible
			data.data.annotations.intervalsV2 = Annotator.annotations.intervals
			// ========================================================================
			// ========================================================================
			// Median beats (v3) — stored under data.annotations.medianBeats.
			// Consolidates per-beat-type fiducial points and per-lead + RMS
			// median-beat waveforms into one field. The legend still lives at
			// data.legend.medianBeatFiducialPoints (describes fiducial-point
			// types — consumed by universalFormatter on load).
			if (!data.data.legend) data.data.legend = {}
			data.data.legend.medianBeatFiducialPoints = buildFiducialPointLegend()
			data.data.annotations.medianBeats = buildMedianBeatsField(Annotator, { dropFiducialTypes })
			// Legacy field intentionally not written; the loader accepts both.
			delete data.data.annotations.medianBeatFiducialPoints
			// ========================================================================
			// data.data.annotations.doctorNote
			data.data.annotations.doctorNote = DoctorNote
			// ========================================================================
			const { data: postData } = await axiosPrivate[METHODS.PUT.toLowerCase()](`recording`, data)
			toast.success("Saved! Reopening Task...")
		} catch (error) {
			if (typeof error === "string") {
				toast.error(error)
			} else {
				toast.error("Error saving recording")
			}
			console.log(error);
			setError(errorHandler(error))
			setLoading(false)
			return
		}
		try {
			await window.loadTurkTask(urlParams["task_id"],urlParams["ecg_id"], false);
		} catch (error) {
			toast.error("Saved, but failed to reload task")
			console.log(error);
			setError(errorHandler(error))
		} finally {
			setLoading(false)
		}
	}

	const onClickSave = async () => {
		// Stale-check each non-empty per-type bucket. Any bucket whose
		// current computed signature diverges from the saved-at-place-time
		// signature is flagged; the confirm-clear dialog lists them and, on
		// confirm, drops only those buckets.
		const byType = Annotator.annotations.medianBeatFiducialPointsByType || {}
		const signaturesByType = Annotator.fiducialPointsSignatureByType || {}
		const staleTypes = []
		for (const slug of Object.keys(byType)) {
			if ((byType[slug] || []).length === 0) continue
			const saved = signaturesByType[slug]
			if (saved == null) continue
			const current = buildFiducialInputSignature(Annotator, slug)
			if (saved !== current) staleTypes.push(slug)
		}
		if (staleTypes.length > 0) {
			setStaleFiducialTypes(staleTypes)
			setStaleFiducialDialogOpen(true)
			return
		}
		await performSave()
	}
	
	// Tab Logic
	const [currentTab, setCurrentTab] = React.useState(isTurkTask ? 1 : 2)
	const handleTabChange = (event, newValue) => {
		setCurrentTab(newValue)
	}

	return (
		<>
			{loading || error?.status === 421 ? (
				<Loader />
			) : error ? (
				<PlaceholderText>Error</PlaceholderText>
			) : (
				<React.Fragment>
					<CardContent sx={{padding: 1.5}}>
						<Grid container>
							{/* Header */}
							<Grid item xs={10} pb={2}>
								<Typography variant="h5" sx={{whiteSpace: "initial" ,overflowWrap: "break-word"}} component="div">
									{isTurkTask ? Annotator.currentTask.name : `Local ${urlParams["ecg_id"].toUpperCase()} File`}
								</Typography>
								{isTurkTask && <Typography variant="p" py={1} component="div">
									Recording id: <b>{Annotator.currentRecordingId}</b>
								</Typography>}
								{Annotator.original.frequency > 0 && <Typography variant="p" pb={1} component="div">
									Sampling rate: <b>{Annotator.original.frequency} Hz</b>
								</Typography>}
								{isTurkTask  ? 
								<Link onClick={copyCurrentUrl}>{window.location.href}</Link>
							 	:
								 <Link>This ECG is not saved on Cloud</Link>
								}
		
							</Grid>
							<Grid item xs={12}>
								<Divider variant="middle" />
							</Grid>
							{Annotator.leadCount == 12 && kai12 && <>
								<Grid item xs={12} py={2}>
									<table style={{width:"100%"}}>
										<tbody>
										<tr>
											<td className={styles.title}>Heart Rate:</td>
											<td className={styles.text}>{kai12.hr} bpm</td>
										</tr>
										<tr>
											<td className={styles.title}>PR Interval:</td>
											<td className={styles.text}>{kai12.pr} ms</td>
										</tr>
										<tr>
											<td className={styles.title}>ORS Duration:</td>
											<td className={styles.text}>{kai12.qrs} ms</td>
										</tr>
										<tr>
											<td className={styles.title}>QT / QTcB / QTcF:</td>
											<td className={styles.text}>{kai12.qt} / {kai12.qtcb} / {kai12.qtcf} ms</td>
										</tr>
										<tr>
											<td className={styles.title}>P / QRS / T Axis:</td>
											<td className={styles.text}>{kai12.pax}° / {kai12.qax}° / {kai12.tax}°</td>
										</tr>
										{Annotator.annotations.algStatement.type !== "" && <>
										<tr>
											<td className={styles.title}>Interpretation: (Unconfirmed)</td>
										</tr>
										<tr>
											<td className={styles.text}>{Annotator.annotations.algStatement.type} selected</td>
										</tr>
										<tr>
											<td className={styles.title}>Rhythm:</td>
										</tr>
										{Annotator.annotations.algStatement.rhythm.map((t,i) => (
											<tr key={`rhy-${i}`}>
												<td className={styles.text}>{t}</td>
											</tr>
										))}
										<tr>
											<td className={styles.title}>Morphology:</td>
										</tr>
										{Annotator.annotations.algStatement.morphology.map((t,i) => (
											<tr key={`morph-${i}`}>
												<td className={styles.text}>{t}</td>
											</tr>
										))}
										</>}
										</tbody>
									</table>
								</Grid>
								<Grid item xs={12}>
									<Divider variant="middle" />
								</Grid>
							</>}
							
							{/* Tabs */}
							<Grid item xs={12}>
								<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
									<Tabs value={currentTab} onChange={handleTabChange} aria-label="Annotator Page Tab">
										<Tab value={1} sx={{ fontWeight: "Bold", display: `${!isTurkTask && "none"}` }} label="Annotate" />
										<Tab value={2} sx={{ fontWeight: "Bold" }} label="JSON" />
									</Tabs>
								</Box>
							</Grid>
							{/* Tab Content */}
							{currentTab === 1 && isTurkTask == true && (
								<>
									{Annotator.leadCount !== 12 && <>
										<Grid item xs={12} py={2}>
											{AlgDet.map((child) => child)}
										</Grid>
										<Grid item xs={12}>
											<Divider variant="middle" />
										</Grid>
									</>}									
									<Grid item xs={12} py={2}>
										<Typography variant="h6" pb={2} component="div">
											<b>Beat Annotation:</b>
										</Typography>
										<Typography variant="p" px={2} pb={1} component="div">
											<b>Labels:</b>
										</Typography>
										{ JSON.parse(JSON.stringify(Annotator?.annotations?.labels?.beats)).map(b => b.name !== "DEFAULT_LEFT_CLICK" && (
											<Chip
												key={b.slug}
												label={`${b.name}${typeof b.slug === 'string' && b.slug.length === 1 ? ` (${b.slug.toLowerCase()})` : ''}`}
												color="primary"
												style={{ margin: "3px", backgroundColor: b.color || "#29292929" }}
											/>
										))}
										<Typography variant="p" px={2} py={1} component="div">
											<b>Markings:</b>
										</Typography>
										{ JSON.parse(JSON.stringify(Annotator?.annotations?.beats)).sort((a, b) => a.i - b.i).map(b => (
											<Chip
												key={b.i}
												label={`${(b.i/Annotator.original.frequency).toFixed(1)}s`}
												color="primary"
												onDelete={() => {
													dispatch(removeBeat(b.i))
												}}
												style={{ margin: "3px", backgroundColor: Annotator?.annotations?.labels?.beats.find((label) => label.slug === b.t)?.color || "#29292929" }}
											/>
										))}
									</Grid>
									<Grid item xs={12}>
										<Divider variant="middle" />
									</Grid>
									<Grid item xs={12} py={2}>
										{(() => {
											const currentBeatMeta = (Annotator?.annotations?.labels?.beats || []).find((b) => b.slug === currentBeatType)
											const currentBeatName = currentBeatMeta?.name || currentBeatType
											return (
												<Typography variant="h6" pb={1} component="div">
													<b>Median Beat Fiducial Points ({currentBeatName}):</b>
												</Typography>
											)
										})()}
										<Typography variant="p" px={2} pb={1} component="div">
											<b>Types:</b>
										</Typography>
										{FIDUCIAL_POINT_TYPES.map(fp => (
											<Chip
												key={fp.slug}
												label={fp.name}
												style={{ margin: "3px", backgroundColor: fp.color, color: "#fff" }}
											/>
										))}
										<Typography variant="p" px={2} py={1} component="div">
											<b>Markings:</b>
										</Typography>
										{currentFiducialPoints.length > 0 ? (
											currentFiducialPoints.map(fp => {
												const fpType = FIDUCIAL_POINT_TYPES.find(t => t.slug === fp.type)
												const signMs = fp.offsetMs > 0 ? `+${Math.round(fp.offsetMs)}` : `${Math.round(fp.offsetMs)}`
												return (
													<Chip
														key={fp.type}
														label={`${fpType?.name || fp.type}: ${signMs} ms`}
														style={{ margin: "3px", backgroundColor: fpType?.color || "#888", color: "#fff" }}
														onDelete={() => {
															dispatch(removeFiducialPoint(fp.type))
														}}
													/>
												)
											})
										) : (
											<Typography variant="body2" color="text.secondary" px={2}>No fiducial points marked</Typography>
										)}
										<Typography variant="p" px={2} py={1} component="div">
											<b>Calculated Intervals:</b>
										</Typography>
										{(() => {
											const intervals = computeFiducialIntervals(currentFiducialPoints)
											const rows = [
												{ label: "P-wave duration", value: intervals.p },
												{ label: "PR interval", value: intervals.pr },
												{ label: "QRS duration", value: intervals.qrs },
												{ label: "ST interval", value: intervals.st },
												{ label: "QT interval", value: intervals.qt },
											].filter(r => r.value != null)
											if (rows.length === 0) {
												return <Typography variant="body2" color="text.secondary" px={2}>No intervals available</Typography>
											}
											return rows.map(r => (
												<Chip
													key={r.label}
													label={`${r.label}: ${Math.round(r.value)} ms`}
													variant="outlined"
													style={{ margin: "3px" }}
												/>
											))
										})()}
										<FormControlLabel
											sx={{ mt: 1, ml: 0, opacity: Annotator.leadCount > 1 ? 1 : 0.5 }}
											control={
												<Switch
													checked={!!Annotator.settings.medianBeatSingleColumn}
													onChange={() => dispatch(setMedianBeatSingleColumn(!Annotator.settings.medianBeatSingleColumn))}
													name="medianBeatSingleColumn"
													disabled={Annotator.leadCount <= 1}
												/>
											}
											label="Single-column median beats"
										/>
										<Typography variant="body2" pt={1} pb={0.5} component="div" sx={{ opacity: (Annotator.settings.medianBeatSingleColumn && Annotator.leadCount > 1) ? 1 : 0.5 }}>
											Vertical spacing: <b>{Math.round(Annotator.settings.medianBeatVerticalSpacing * 100)}%</b>
										</Typography>
										<Slider
											size="small"
											value={Annotator.settings.medianBeatVerticalSpacing}
											min={0}
											max={1}
											step={0.05}
											disabled={!Annotator.settings.medianBeatSingleColumn || Annotator.leadCount <= 1}
											onChange={(e, v) => dispatch(setMedianBeatVerticalSpacing(v))}
										/>
										{Annotator.leadCount !== 12 && (() => {
											const options = beatTypeDropdownOptions
											if (options.length === 0) return null
											return (
												<Box sx={{ mt: 2 }}>
													<Typography variant="body2" pb={0.5} component="div">
														Compute median from beat type:
													</Typography>
													<FormControl size="small" fullWidth>
														<Select
															value={currentBeatType}
															onChange={(e) => dispatch(setMedianBeatBeatType(e.target.value))}
														>
															{options.map((opt) => (
																<MenuItem key={opt.slug} value={opt.slug}>
																	<Box component="span" sx={{
																		display: "inline-block",
																		width: 10,
																		height: 10,
																		borderRadius: "50%",
																		background: opt.color,
																		mr: 1,
																		verticalAlign: "middle",
																	}} />
																	{opt.name} ({opt.count})
																</MenuItem>
															))}
														</Select>
													</FormControl>
												</Box>
											)
										})()}
										{Annotator.leadCount !== 12 && (
											<>
												<Typography variant="body2" pt={1} pb={0.5} component="div">
													Window (left of R-peak): <b>{currentWindow.leftMs} ms</b>
												</Typography>
												<Slider
													size="small"
													value={currentWindow.leftMs}
													min={200}
													max={1200}
													step={50}
													onChange={(e, v) => dispatch(setMedianBeatLeftMs(v))}
												/>
												<Typography variant="body2" pt={1} pb={0.5} component="div">
													Window (right of R-peak): <b>{currentWindow.rightMs} ms</b>
												</Typography>
												<Slider
													size="small"
													value={currentWindow.rightMs}
													min={200}
													max={1200}
													step={50}
													onChange={(e, v) => dispatch(setMedianBeatRightMs(v))}
												/>
											</>
										)}
									</Grid>
									<Grid item xs={12}>
										<Divider variant="middle" />
									</Grid>
									<Grid item xs={12} py={2}>
										<Typography variant="h6" pb={1} component="div">
											<b>Interval Annotations:</b>
										</Typography>
										{Annotator?.annotations?.intervals?.length > 0 ? (
											[...Annotator.annotations.intervals].sort((a, b) => a.start - b.start).map((intr) => {
												const startS = Number(intr?.time?.start)
												const endS = Number(intr?.time?.end)
												const hasTimes = Number.isFinite(startS) && Number.isFinite(endS)
												const durationMs = hasTimes ? ((endS - startS) * 1000).toFixed(0) : null
												const bpm = intr?.bpm
												return (
													<Box key={intr.start} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, py: 0.5, borderBottom: "1px solid #eee" }}>
														<Box sx={{ flex: 1, minWidth: 0 }}>
															<FormControl size="small" variant="standard" sx={{ minWidth: 140 }}>
																<Select
																	value={intr?.type?.name ?? ""}
																	displayEmpty
																	renderValue={(v) => v || "Unlabeled"}
																	sx={{
																		backgroundColor: intr?.type?.color || "#29292929",
																		borderRadius: 16,
																		px: 1.5,
																		'&:before, &:after': { display: 'none' },
																		'.MuiSelect-select': { paddingTop: 0.5, paddingBottom: 0.5 },
																	}}
																	onChange={(e) => {
																		if (e.target.value === "") {
																			dispatch(updateIntr({ index: intr.start, value: { type: { id: "", name: "" } } }))
																			return
																		}
																		const chosen = Annotator?.annotations?.labels?.intervals?.find(r => r.name === e.target.value)
																		if (chosen) dispatch(updateIntr({ index: intr.start, value: { type: chosen } }))
																	}}
																>
																	<MenuItem value=""><em>Unlabeled</em></MenuItem>
																	{(Annotator?.annotations?.labels?.intervals || []).map((r, i) => (
																		<MenuItem key={r.name ?? i} value={r.name}>{r.name}</MenuItem>
																	))}
																</Select>
															</FormControl>
															{hasTimes && (
																<Typography variant="caption" component="div" color="text.secondary">
																	{startS.toFixed(2)}s → {endS.toFixed(2)}s ({durationMs} ms)
																</Typography>
															)}
															{bpm && (
																<Typography variant="caption" component="div" color="text.secondary">
																	BPM: {bpm.average} ({bpm.min}–{bpm.max})
																</Typography>
															)}
														</Box>
														<Chip
															aria-label="Delete interval"
															label="×"
															size="small"
															onClick={() => dispatch(removeIntr(intr.start))}
															sx={{ ml: 1, cursor: "pointer" }}
														/>
													</Box>
												)
											})
										) : (
											<Typography variant="body2" color="text.secondary" px={1}>No intervals annotated</Typography>
										)}
									</Grid>
									<Grid item xs={12}>
										<Divider variant="middle" />
									</Grid>
									<Grid item xs={12} py={2}>
										<Typography variant="h6" pb={1} component="div">
											<b>Global Annotation:</b>
										</Typography>
										<FormGroup>
											{Annotator.leadCount !== 12 && (
												<>
													<FormControlLabel
														key="isInverted"
														control={
															<Checkbox
																checked={!!Annotator.settings.inverted}
																onChange={() => dispatch(setInverted(!Annotator.settings.inverted))}
																name="isInverted"
															/>
														}
														label="INVERTED"
													/>
													<Divider sx={{ my: 1 }} />
												</>
											)}
											{Object.keys(AlgAnnotations).map((ann) => (
												<FormControlLabel
													key={ann}
													control={
														<Checkbox
															checked={AlgAnnotations[ann]}
															onChange={() => {setAlgAnnotations({...AlgAnnotations, [ann]: !AlgAnnotations[ann]})}}
															name={ann}
														/>
													}
													label={ann}
												/>
											))}
										</FormGroup>
									</Grid>
									<Grid item xs={12}>
										<Divider variant="middle" />
									</Grid>
									<Grid item xs={12} py={2}>
										<Button
											variant="outlined"
											fullWidth
											onClick={(e) => setBulkAnchorEl(e.currentTarget)}
											endIcon={<ArrowDropDownIcon />}
											sx={{ typography: "h6", fontWeight: "bold", textTransform: "none" }}
										>
											Bulk Actions
										</Button>
										<Menu
											anchorEl={bulkAnchorEl}
											open={bulkMenuOpen}
											onClose={() => setBulkAnchorEl(null)}
										>
											<MenuItem onClick={handleClearAll}>Clear All</MenuItem>
											{(Annotator?.annotations?.labels?.beats || [])
												.filter((lbl) => lbl.name !== "DEFAULT_LEFT_CLICK")
												.map((lbl) => (
													<MenuItem key={lbl.slug} onClick={() => handleSetBeats(lbl.slug)}>
														Set Beats: {lbl.name}
													</MenuItem>
												))}
										</Menu>
									</Grid>
									<Grid item xs={12}>
										<Divider variant="middle" />
									</Grid>
									<Grid item xs={12} py={2}>
										<TextField
											multiline
											fullWidth
											label="Doctor Note"
											rows={4}
											value={DoctorNote}
											onChange={e => setDoctorNote(e.target.value)}
										/>
									</Grid>
									<Grid item xs={12}>
										<Divider variant="middle" />
									</Grid>
									<Grid item xs={6} sm={6} md={6} mb={2} p={1}>
										<Button
											variant="contained"
											sx={{ whiteSpace: "nowrap", width: "100%" }}
											size="large"
											onClick={() => {
												onClickSave()
											}}
										>
											Save
										</Button>
									</Grid>
									<Grid item xs={6} sm={6} md={6} mb={2} p={1}>
										<Button variant="contained" sx={{ whiteSpace: "nowrap", width: "100%" }} size="large" onClick={() => window.loadTurkTask(urlParams["task_id"],urlParams["ecg_id"], false)}>
											Clear
										</Button>
									</Grid>
									<Grid item xs={12}>
										<Divider variant="middle" />
									</Grid>
									<Grid item xs={10} pb={2}>
										<Typography variant="h6" py={1} component="div">
											Audit Log
										</Typography>
										{Annotator?.annotations?.audit &&
											Annotator?.annotations?.audit.map((log, i) => (
												<Typography key={i} variant="p" component="div">
													{new Date(log.time).toLocaleDateString()} - <b>{log.person}</b> {log.message ? `: ${log.message}` : ""}
												</Typography>
											))}
									</Grid>
								</>
							)}

							{currentTab === 2 && (
								<Grid item xs={12} my={2}>
									<Typography variant="h6" component="div">
										{`JSON File Content: `}
									</Typography>
									<JsonViewer data={FileRawData} />
								</Grid>
							)}

						</Grid>
					</CardContent>
				</React.Fragment>
			)}
			<Dialog open={staleFiducialDialogOpen} onClose={() => setStaleFiducialDialogOpen(false)}>
				<DialogTitle>Fiducial points are out of date</DialogTitle>
				<DialogContent>
					<DialogContentText>
						The median beat waveforms have changed since these fiducial points were placed, so the marked positions no longer correspond to the current waveforms.
						{staleFiducialTypes.length > 0 && (
							<>
								{" "}
								Affected beat type{staleFiducialTypes.length === 1 ? "" : "s"}:{" "}
								<b>
									{staleFiducialTypes
										.map((slug) => {
											const beatMeta = (Annotator?.annotations?.labels?.beats || []).find((b) => b.slug === slug)
											return beatMeta?.name || slug
										})
										.join(", ")}
								</b>
								.
							</>
						)}
						{" "}If you continue, only the affected beat type's fiducial points will be cleared before saving.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setStaleFiducialDialogOpen(false)}>Cancel</Button>
					<Button
						color="warning"
						variant="contained"
						onClick={() => {
							const toDrop = staleFiducialTypes
							setStaleFiducialDialogOpen(false)
							setStaleFiducialTypes([])
							performSave({ dropFiducialTypes: toDrop })
						}}
					>
						Clear and save
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default RightPanel
